import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { editInvestigation2 } from "services/prosecutorDecipilineService";
import { Complaints, Investigation } from "types";
import { LoadingIcon } from "utils/constant";
import { INVESTIGATION_STATUS } from "utils/constantValues";
import "../styles/AddEvidenceModal.css";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  investigationData: Investigation;
  onRefresh: any;
  latestChargeDoc: any;
}

export default function AddChargeRegistrationModal({
  isOpen,
  setIsOpen,
  complaintData,
  investigationData,
  onRefresh,
  latestChargeDoc,
}: ModalProps) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [signatureFile, setSignatureFile] = useState<File | null>();
  const [formState, setFormState] = useState<Investigation>({
    registration_number: "",
    date: "",
    status: INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR,
    complaint: null,
    files: [] as File[],
  });

  const [chargeDocumentFile, setChargeDocumentFile] = useState<File | null>();
  const [stampUrl, setStampUrl] = useState<string | null>(null);
  const [stampBlob, setStampBlob] = useState<Blob | null>(null);

  useEffect(() => {
    const fetchChargeFile = async () => {
      if (latestChargeDoc) {
        if (latestChargeDoc?.document_uuid) {
          const documentUrl = `${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document/${latestChargeDoc.document_uuid}/`;
          try {
            const response = await fetch(documentUrl, {
              headers: {
                Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
              },
            });
            if (!response.ok)
              throw new Error("Failed to fetch memorandum file");
            const blob = await response.blob();
            // Try to infer the file name from the document or fallback
            const fileName =
              latestChargeDoc.file_name ||
              "memorandum_of_disciplinary_appeal.pdf";
            const file = new File([blob], fileName, { type: blob.type });
            setChargeDocumentFile(file);
          } catch (error) {
            console.error("Failed to fetch memorandum file", error);
            setChargeDocumentFile(null);
          }
        } else {
          setChargeDocumentFile(null);
        }
      } else {
        setChargeDocumentFile(null);
      }
    };
    fetchChargeFile();
  }, [latestChargeDoc]);

  useEffect(() => {
    let objectUrl: string | null = null;

    const loadStamp = async () => {
      try {
        // Construct the actual API URL – adjust to your real endpoint
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL_2}organization/service-stamp/read_stamp/?admin_unit_uuid=${user.admin_unit}&service_name=pdms`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            }, // if needed
          },
        );
        if (!response.ok) throw new Error("Failed to fetch stamp");
        const blob = await response.blob(); // ✅ direct binary, no string corruption
        setStampBlob(blob);
        objectUrl = URL.createObjectURL(blob);
        setStampUrl(objectUrl);
      } catch (error) {
        console.error("Failed to load stamp", error);
      }
    };

    if (isOpen && user) loadStamp();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, user]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const signatureFileInputRef = useRef<HTMLInputElement>(null);
  const chargeFileInputRef = useRef<HTMLInputElement>(null);

  const triggerSignatureFileInput = () => {
    signatureFileInputRef.current?.click();
  };

  const triggerChargeFileInput = () => {
    chargeFileInputRef.current?.click();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationError(null);
    setLoading(true);

    // Validations...
    if (!formState.registration_number || !formState.date) {
      setValidationError("Registration number and date are required.");
      setLoading(false);
      return;
    }
    if (!stampBlob) {
      setValidationError("Stamp is required.");
      setLoading(false);
      return;
    }
    if (!signatureFile) {
      setValidationError("Signature file is required.");
      setLoading(false);
      return;
    }
    if (!chargeDocumentFile && !latestChargeDoc) {
      setValidationError("Charge document file is required.");
      setLoading(false);
      return;
    }

    if (!investigationData) {
      setValidationError("Investigation data is missing.");
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("registration_number", formState.registration_number);
    data.append("date", formState.date);
    data.append("status", formState.status ?? "");
    data.append("signature1", stampBlob, "stamp.png");
    data.append("signature2", signatureFile);

    // Use uploaded charge document file
    if (chargeDocumentFile) {
      data.append("charge_documents", chargeDocumentFile);
    }

    const investigation_id = investigationData?.uu_id;
    if (!investigation_id) {
      setValidationError("Investigation ID missing");
      setLoading(false);
      return;
    }

    try {
      await editInvestigation2(data, investigation_id, true);
      onRefresh();
      setIsOpen(false);
      showToast("Successfully Created", "success");
      setFormState({
        registration_number: "",
        date: "",
        complaint: null,
        status: INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast("Submission error", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={"Add Charge Registration No."}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        {validationError && (
          <div className="mb-4 text-red-600 font-medium text-sm border border-red-200 bg-red-50 rounded p-2">
            {validationError}
          </div>
        )}
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="date"
              placeholder="Eg. June 12, 2014"
              value={formState.date ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Registration No
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="text"
              name="registration_number"
              placeholder="Eg. XX000001"
              value={formState.registration_number}
              onChange={handleChange}
            />
          </div>

          {/* <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Charge Feedback
            </label>
            <textarea
              required
              rows={7}
              name="feedback"
              value={formState.feedback}
              onChange={handleChange}
              placeholder="Details here..."
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div> */}
          {/* <label className="block text-sm font-medium text-gray-700 mb-1">
            Charge File
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">Upload Files</p>
            {(formState?.files?.length ?? 0) > 0 && (
              <div className="file-list">
                {formState?.files?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div> */}

          <label className="block text-sm font-medium text-gray-700 mb-1">
            Signature
          </label>
          <div className="file-upload-area" onClick={triggerSignatureFileInput}>
            <input
              type="file"
              ref={signatureFileInputRef}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setSignatureFile(e.target.files[0]);
                }
              }}
              accept=".jpg,.png,.jpeg,.gif"
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">Upload Files</p>
            {signatureFile && (
              <div className="file-list">
                {signatureFile && (
                  <div className="file-item">{signatureFile.name}</div>
                )}
              </div>
            )}
          </div>
          {/* <label className="block text-sm font-medium text-gray-700 mb-1 mt-3">
            Charge Document
          </label>
          <div className="file-upload-area" onClick={triggerChargeFileInput}>
            <input
              type="file"
              ref={chargeFileInputRef}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setChargeDocumentFile(e.target.files[0]);
                }
              }}
              accept=".pdf,.doc,.docx"
              style={{ display: "none" }}
            />
            <p className="upload-text">Upload Files</p>
            {chargeDocumentFile && (
              <div className="file-list">
                <div className="file-item">{chargeDocumentFile.name}</div>
              </div>
            )}
          </div> */}
        </section>

        {/* DocumentViewerModal removed - now using file upload instead */}

        {stampUrl && (
          <div className="my-4 flex items-center justify-center">
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 shadow-sm">
              <svg
                className="w-6 h-6 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-green-700 font-semibold">
                Stamp fetched successfully
              </span>
            </div>
          </div>
        )}

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              Submitting...
            </div>
          ) : (
            "Submit"
          )}
        </button>
      </form>
    </SideModal>
  );
}
